import { Dispatch } from 'react-redux';
import { StoreState } from '../../reducers';
import { Action } from '../../actions';
import store from '../../store';
import setLocales from '../../store/setLocales';
import Utils from '../../utils';
import UserAgentUtils from '../../utils/userAgent';
import ApiUtils from '../../utils/api';
import StatsUtils from '../../utils/statistics';
import LocalStorageManager from '../../utils/localStorage/manager';
import IndexedDBManager from '../../utils/indexedDB/manager';
import { Api } from '../../typings/api';
import { Menu, MenuItemType } from '../../typings/menu';
import { loadPreviousCredentials, updateCredentials, setNeedCredentials } from '../Authentication/actions';
import { sendInitializationError, loadOfflineManager, initializeOfflineManager, downloadBrandThumbIfNeeded, OfflineManagerActionKeys } from '../OfflineManager/actions';
import { loadPreviousSelection } from '../Selection/actions';
import { AppType } from '../App/reducer';
import { loadPreviousDownloads } from '../DownloadManager/actions';
import { getAllDriveItems, getOrFetchDriveItemMeta, setFoldersDisplayType } from '../DriveItemList/actions';
import { defaultGalleryMenu, defaultAppMenu } from '../MenuManager/constants';
import { loadNewItemsStore } from '../DriveItemList/New/actions';
import { createNotification } from '../NotificationBar/actions';
import RoutesUtils from '../../utils/routes';
import AuthUtils from '../../utils/authentication';
import { ConnectionStatus, AuthenticationMode } from '../Authentication/reducer';
import { closeDownloadAllPopupIfNeeded } from '../DownloadAllPopup/actions';
import { BrandThumbConfigLocation } from '../OfflineManager/reducer';
import { loadPendingTasksManager } from '../PendingTasksManager/actions';
import { loadReadStatusStore } from '../ReadStatus/actions';
import { AppInfosActionKeys } from '../App/actions';
import { getCustomerContext, getProfileData } from '../ProfilePage/actions';
import { updateCredentials as updateMultiAppCredentials } from '../MultiAppAuthentication/actions';
import { AuthMethod } from '../MultiAppAuthentication/constants';
import TrackerManager from '../../utils/statistics/trackerManager';
import { DownloadGroupName } from '../DownloadManager/reducer';

export enum GalleryInfosActionKeys {
	REQUEST_CONFIG = '@@gallery/REQUEST_CONFIG',
	RECEIVE_CONFIG = '@@gallery/RECEIVE_CONFIG',
	REQUEST_LOCAL_TEMPLATE = '@@gallery/REQUEST_LOCAL_TEMPLATE',
	RECEIVE_LOCAL_TEMPLATE = '@@gallery/RECEIVE_LOCAL_TEMPLATE',
	REQUEST_REMOTE_TEMPLATE = '@@gallery/REQUEST_REMOTE_TEMPLATE',
	RECEIVE_REMOTE_TEMPLATE = '@@gallery/RECEIVE_REMOTE_TEMPLATE',
	SET_CUSTOM_CSS = '@@gallery/SET_CUSTOM_CSS',
	FORCE_VIEW_UPDATE = '@@gallery/FORCE_VIEW_UPDATE',
	SET_FORCE_SPLASH_SCREEN = '@@gallery/SET_FORCE_SPLASH_SCREEN',
	UPDATE_TEMPLATE_BRAND = '@@gallery/UPDATE_TEMPLATE_BRAND',
	UPDATE_TEMPLATE_COMMONSTYLES = '@@gallery/UPDATE_TEMPLATE_COMMONSTYLES',
}

interface RequestConfigAction extends Action {
	type: GalleryInfosActionKeys.REQUEST_CONFIG;
}

interface ReceiveConfigAction extends Action {
	type: GalleryInfosActionKeys.RECEIVE_CONFIG;
	data: Api.Config;
}

interface RequestLocalTemplateAction extends Action {
	type: GalleryInfosActionKeys.REQUEST_LOCAL_TEMPLATE;
}

interface ReceiveLocalTemplateAction extends Action {
	type: GalleryInfosActionKeys.RECEIVE_LOCAL_TEMPLATE;
	data: Api.Template;
}

interface RequestRemoteTemplateAction extends Action {
	type: GalleryInfosActionKeys.REQUEST_REMOTE_TEMPLATE;
}

interface ReceiveRemoteTemplateAction extends Action {
	type: GalleryInfosActionKeys.RECEIVE_REMOTE_TEMPLATE;
	data: Api.Template;
	isAfterLogout?: boolean; // Flag to indicate if this is after explicit logout
}

interface SetCustomCSSAction extends Action {
	type: GalleryInfosActionKeys.SET_CUSTOM_CSS;
	data: string;
}

interface ForceUpdateAction extends Action {
	type: GalleryInfosActionKeys.FORCE_VIEW_UPDATE;
}

interface SetForceSplashScreen extends Action {
	type: GalleryInfosActionKeys.SET_FORCE_SPLASH_SCREEN;
	isShown: boolean;
}

interface UpdateTemplateBrandLogo extends Action {
	type: GalleryInfosActionKeys.UPDATE_TEMPLATE_BRAND;
	value: string;
}

interface UpdateTemplateCommonStyles extends Action {
	type: GalleryInfosActionKeys.UPDATE_TEMPLATE_COMMONSTYLES;
	value: Api.CommonStyles;
}

export type GalleryInfosAction = RequestConfigAction
	| ReceiveConfigAction
	| RequestLocalTemplateAction
	| ReceiveLocalTemplateAction
	| RequestRemoteTemplateAction
	| ReceiveRemoteTemplateAction
	| SetCustomCSSAction
	| ForceUpdateAction
	| SetForceSplashScreen
	| UpdateTemplateBrandLogo
	| UpdateTemplateCommonStyles;

import { beginGallerySession } from '../../utils/sessionCancellation';

export const initializeGallery = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const { appInfos, authentication } = getState();

	if (appInfos.type === AppType.Single) {
		beginGallerySession();
	}

	const { mode } = authentication.userInfos;

	await dispatch(initIsOnline());

	if (appInfos.type === AppType.Single) {
		await dispatch(fetchLocalGalleryInfos());
		await migrateAppContainerLocalStorageToIndexedDB();
	}

	await dispatch(RemoveForceSplashScreenAfterDelay());

	if (!AuthUtils.isConnecting(mode)) {
		await dispatch(loadPreviousCredentials());
	}

	// Try to fetch template, but don't let it block initialization if offline and no cache
	// This allows loadPreviousStates() to run and load offline manager state
	try {
		await dispatch(fetchRemoteTemplate());
	} catch (error) {
		// If fetchRemoteTemplate fails (e.g., offline with no cached template), continue initialization
		// loadPreviousStates() will load offline manager state which can handle the offline case
		console.warn('Failed to fetch remote template, continuing initialization:', error);
	}

	await dispatch(loadPreviousStates());

	if (Utils.shouldGetProfileData()) {
		try {
			await dispatch(getCustomerContext());
		} catch (error) {
			console.warn('Failed to get customer context (may be offline), continuing:', error);
		}

		const { galleryInfos: { config: { clientId } } } = getState();
		try {
			await dispatch(getProfileData(clientId));
		} catch (error) {
			console.warn('Failed to get profile data (may be offline), continuing:', error);
		}
	}

	const { galleryInfos: { template: { cookie } } } = getState();
	const isGdprEnabled = !!cookie && cookie.gdpr === 'true';

	if (!Utils.isEmbedMode() && !isGdprEnabled) {
		await dispatch(initStatistics());
	}

	const { galleryInfos } = getState();
	const storedUserLocale = await IndexedDBManager.getUserLocale(true);
	const templateLocale = !appInfos.userLocale && galleryInfos.template ? galleryInfos.template.locale : undefined;
	const embedModeLocale = Utils.isEmbedMode()
		? Utils.getQueryParamFromUrl('locale')
		: undefined;
	const localeToSet = embedModeLocale || storedUserLocale || templateLocale;

	if (localeToSet) {
		setLocales(store, localeToSet);
	}

	if (UserAgentUtils.isMobileApp && UserAgentUtils.isOnline()) {
		IndexedDBManager.setGalleryInfos(galleryInfos).then();
	}

	if (Utils.isMaster(galleryInfos.config)) {
		try {
			await dispatch(getAllDriveItems());
		} catch (error) {
			console.warn('getAllDriveItems failed (may be offline), continuing:', error);
		}
	}

	const offlineManagerResult = dispatch(initializeOfflineManager());
	// When offline, initializeOfflineManager returns a promise that we should await
	// to ensure drive items are loaded before initializeGallery completes
	const isPromise = offlineManagerResult && typeof (offlineManagerResult as Promise<{}>).then === 'function';
	if (!UserAgentUtils.isOnline() && isPromise) {
		try {
			await (offlineManagerResult as Promise<{}>);
		} catch (error) {
			console.error('initializeOfflineManager failed (offline):', error);
			// Continue anyway to not block initialization
		}
	}

	try {
		await dispatch(fetchMenuItemsMeta());
	} catch (error) {
		console.warn('fetchMenuItemsMeta failed, continuing:', error);
	}

	try {
		await dispatch(setFoldersDisplayType());
	} catch (error) {
		console.warn('setFoldersDisplayType failed, continuing:', error);
	}

	const { appInfos: updatedAppInfos } = getState();

	if (!UserAgentUtils.isMobileApp && Utils.isMultiApp(updatedAppInfos)) {
		try {
			const { login, token } = await ApiUtils.refreshSession();

			if (login) {
				await dispatch(updateMultiAppCredentials({
					method: AuthMethod.Webpublication,
					login,
					token,
					lastApiCallTimestamp: Date.now(),
				}));
			}
		} catch (error) {
			console.error('Error while refreshing session', error);
		}
	}

	if (!UserAgentUtils.isMobileApp) {
		// legacy
		LocalStorageManager.removeUsername();
		LocalStorageManager.removePassword();
	}

	const { galleryInfos: { config } } = getState();
	const trackerManager = TrackerManager.getInstance();
	trackerManager.trackProperty('account_id', config.clientId.toString());
	trackerManager.trackProperty('app_id', config.applicationGallery.toString());
};

// At first run of the current app container, move any existing localStorage data to IndexedDB
export const migrateAppContainerLocalStorageToIndexedDB = async (): Promise<void> => {
	const wasAppContainerIndexedDBInitialized = await IndexedDBManager.wasIndexedDBInitialized(true);
	const localStorageGalleryPrefix = LocalStorageManager.getGalleryPrefix();
	const indexDBGalleryPrefix = IndexedDBManager.getGalleryPrefix();

	if (!wasAppContainerIndexedDBInitialized && localStorageGalleryPrefix && indexDBGalleryPrefix) {
		let allOk = true;

		const prevLocalStorageDownloads = LocalStorageManager.getDownloads();
		const prevLocalStorageDownloadGroups = LocalStorageManager.getDownloadGroups();
		const prevLocalStoragePendingSelectedDownloads = LocalStorageManager.getPendingSelectedDownloads();
		const prevLocalStorageDownloadPopupDontAskAgain = LocalStorageManager.getDownloadPopupDontAskAgain();
		const prevLocalStorageDisplayType = LocalStorageManager.getDisplayType();
		const prevLocalStorageFoldersDisplayType = LocalStorageManager.getFoldersDisplayType();
		const prevLocalStorageSelection = LocalStorageManager.getSelection();
		const prevLocalStorageNotesSelection = LocalStorageManager.getNotesSelection();
		const prevLocalStorageNewItems = LocalStorageManager.getNewItemsStore();
		const prevLocalStorageReadStatus = LocalStorageManager.getReadStatusStore();
		const prevLocalStoragePendingTasks = LocalStorageManager.getPendingTasks();
		const prevLocalStorageGalleryInfos = LocalStorageManager.getStoreGalleryInfos();
		const prevLocalStorageDriveList = LocalStorageManager.getStoreDriveList();
		const prevLocalStorageOfflineManager = LocalStorageManager.getStoreOfflineManager();
		const prevLocalStorageCredentials = LocalStorageManager.getCredentials();
		const prevLocalStorageTrackingQueue = LocalStorageManager.getTrackingQueue();
		const prevLocalStorageUserLocale = LocalStorageManager.getUserLocale(true);

		if (prevLocalStorageDownloads) {
			try {
				await IndexedDBManager.setDownloads(prevLocalStorageDownloads);
				LocalStorageManager.removeDownloads();
			} catch (error) {
				console.error('Failed to migrate downloads to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageDownloadGroups) {
			try {
				await IndexedDBManager.setDownloadGroups(DownloadGroupName.ALL, prevLocalStorageDownloadGroups.all);
				await IndexedDBManager.setDownloadGroups(DownloadGroupName.IMPORTANT, prevLocalStorageDownloadGroups.important);
				await IndexedDBManager.setDownloadGroups(DownloadGroupName.SELECTED, prevLocalStorageDownloadGroups.selected);
				LocalStorageManager.removeDownloadGroups();
			} catch (error) {
				console.error('Failed to migrate download groups to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStoragePendingSelectedDownloads) {
			try {
				await IndexedDBManager.setPendingSelectedDownloads(prevLocalStoragePendingSelectedDownloads);
				LocalStorageManager.removePendingSelectedDownloads();
			} catch (error) {
				console.error('Failed to migrate pendingSelectedDownloads to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageDownloadPopupDontAskAgain) {
			try {
				await IndexedDBManager.setDownloadPopupDontAskAgain();
				LocalStorageManager.removeDownloadPopupDontAskAgain();
			} catch (error) {
				console.error('Failed to migrate downloadPopupDontAskAgain to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageDisplayType) {
			try {
				await IndexedDBManager.setDisplayType(prevLocalStorageDisplayType);
				LocalStorageManager.removeDisplayType();
			} catch (error) {
				console.error('Failed to migrate displayType to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageFoldersDisplayType) {
			try {
				await Promise.all(
					Object.entries(prevLocalStorageFoldersDisplayType).map(([folderId, displayType]) =>
						IndexedDBManager.setFolderDisplayType(folderId, displayType)
					)
				);
				LocalStorageManager.resetFoldersDisplayType();
			} catch (error) {
				console.error('Failed to migrate foldersDisplayType to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageSelection) {
			try {
				await IndexedDBManager.setSelection(prevLocalStorageSelection);
				LocalStorageManager.removeSelection();
			} catch (error) {
				console.error('Failed to migrate selection to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageNotesSelection) {
			try {
				await IndexedDBManager.setNotesSelection(prevLocalStorageNotesSelection);
				LocalStorageManager.removeNotesSelection();
			} catch (error) {
				console.error('Failed to migrate notesSelection to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageNewItems) {
			try {
				await IndexedDBManager.setNewItems(prevLocalStorageNewItems);
				LocalStorageManager.removeNewItems();
			} catch (error) {
				console.error('Failed to migrate newItems to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageReadStatus) {
			try {
				await IndexedDBManager.setReadStatus(prevLocalStorageReadStatus);
				LocalStorageManager.removeReadStatus();
			} catch (error) {
				console.error('Failed to migrate readStatus to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStoragePendingTasks) {
			try {
				await IndexedDBManager.setPendingTasks(prevLocalStoragePendingTasks);
				LocalStorageManager.removePendingTasks();
			} catch (error) {
				console.error('Failed to migrate pendingTasks to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageGalleryInfos) {
			try {
				await IndexedDBManager.setGalleryInfos(prevLocalStorageGalleryInfos);
				LocalStorageManager.removeStoreGalleryInfos();
			} catch (error) {
				console.error('Failed to migrate galleryInfos to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageDriveList) {
			try {
				await IndexedDBManager.setStoreDriveList(prevLocalStorageDriveList);
				LocalStorageManager.removeStoreDriveList();
			} catch (error) {
				console.error('Failed to migrate driveList to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageOfflineManager) {
			try {
				await IndexedDBManager.setStoreOfflineManager(prevLocalStorageOfflineManager);
				LocalStorageManager.removeStoreOfflineManager();
			} catch (error) {
				console.error('Failed to migrate offlineManager to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageCredentials) {
			try {
				await IndexedDBManager.setCredentials(prevLocalStorageCredentials);
				LocalStorageManager.removeCredentials();
			} catch (error) {
				console.error('Failed to migrate credentials to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageTrackingQueue) {
			try {
				await IndexedDBManager.setTrackingQueue(prevLocalStorageTrackingQueue);
				LocalStorageManager.removeTrackingQueue();
			} catch (error) {
				console.error('Failed to migrate trackingQueue to IndexedDB', error);
				allOk = false;
			}
		}

		if (prevLocalStorageUserLocale) {
			try {
				await IndexedDBManager.setUserLocale(prevLocalStorageUserLocale, true);
				LocalStorageManager.removeUserLocale(true);
			} catch (error) {
				console.error('Failed to migrate userLocale (prefixed) to IndexedDB', error);
				allOk = false;
			}
		}

		if (allOk) {
			await IndexedDBManager.setIndexedDBInitialized(true);
		} else {
			console.error('Failed to migrate app container localStorage to IndexedDB');
		}
	}
};

const initIsOnline = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{}> => {
	if (UserAgentUtils.isMobileApp) {
		return UserAgentUtils.isOnlinePromise();
	}
	return Promise.resolve({});
};

const areCredentialsNeeded = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): void | Promise<void | {}> => {
	const { authentication, appInfos } = getState();

	if (appInfos.type === AppType.Multiple) {
		return Promise.resolve();
	}

	const { mode } = authentication.userInfos;
	const needSecurityCheck = !mode || AuthUtils.isNotConnected(mode);


	if (AuthUtils.needReconnecting(mode)) { // SAML on IOS need to be automatically reauthenticated
		RoutesUtils.historyPush(RoutesUtils.getPathAuthentication());
		return Promise.reject(undefined);
	}

	if (needSecurityCheck) {
		return ApiUtils.getSecurityType()
			.then(value => {
				if (value) {
					dispatch(setNeedCredentials(true, value.securityType, value.samlUrl));
					return Promise.reject(undefined);
				}
				return Promise.resolve();
			});
	}
	return Promise.resolve();
};

const fetchMenuItemsMeta = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<{}> => {
	const { galleryInfos, authentication } = getState();
	const { template } = galleryInfos;
	const { needCredentials, mode } = authentication.userInfos;
	const hasAccess = !needCredentials || needCredentials && AuthUtils.isConnected(mode);

	if (template.menu && hasAccess && UserAgentUtils.isOnline()) {
		const customMenuItems = getCustomMenuItemsIds(template.menu);

		customMenuItems.forEach(item => dispatch(getOrFetchDriveItemMeta(item, false)));
	}
	return Promise.resolve({});
};

const getCustomMenuItemsIds = (menuConfig: Menu): number[] => {
	let customMenuItems: number[] = [];

	Object.keys(menuConfig).forEach(menuFormat => {
		Object.keys(menuConfig[menuFormat]).forEach(menuPosition => {
			menuConfig[menuFormat][menuPosition].forEach((menuItem, index) => {
				if (menuItem.type === MenuItemType.Asset || menuItem.type === MenuItemType.Folder || menuItem.type === MenuItemType.Page) {
					const targetId = Utils.getMediaId(menuItem.target);
					if (targetId > 0) {
						customMenuItems.push(targetId);
					}
				}

				if (menuItem.icon) {
					const iconId = Utils.getMediaId(menuItem.icon);
					if (iconId > 0) {
						customMenuItems.push(iconId);
					}
				}
			});
		});
	});

	return customMenuItems.filter((item, index, self) => index === self.indexOf(item));
};

const fetchLocalGalleryInfos = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void | {}> => {
	const { galleryInfos } = getState();
	if (galleryInfos.isConfigLoaded) {
		return Promise.resolve(undefined);
	}
	dispatch(requestConfig());
	return Utils.fetchJson(`./.gallery/config/config.json`, 'GET', undefined, true)
		.then((config: Api.Config) => {
			if (Utils.isEmbedMode()) {
				// for embed mode a special application is used with no clientId and applicationGallery params in config
				// it allows any user to connect and use these params from url
				const clientId = +Utils.getQueryParamFromUrl('clientId');
				const rootDriveId = +Utils.getQueryParamFromUrl('rootDriveId');
				config = {
					...config,
					clientId,
					applicationGallery: rootDriveId,
				};
			}

			LocalStorageManager.setGalleryPrefix(config.applicationGallery);
			IndexedDBManager.setGalleryPrefix(config.applicationGallery);

			if (config.type && config.type === Api.DriveItemType.AppContainer) {
				dispatch({ type: AppInfosActionKeys.SET_APP_TYPE, appType: AppType.Multiple });
			}

			dispatch(receiveConfig(config));
		})
		.then(() => dispatch(loadPreviousHomeLogo()))
		.then(() => dispatch(fetchTemplateOrLoadPreviousOne()))
		.then((template: Api.Template) => {
			if (!template.menu) {
				template.menu = defaultGalleryMenu;
			}
			dispatch(receiveLocalTemplate(template));
			setLocales(store, template.locale);

			return dispatch(downloadBrandThumbIfNeeded(BrandThumbConfigLocation.LOCAL_CONFIG, template, true));
		})
		.then(() => {
			const { hasCustomCSS } = getState().galleryInfos.template;
			if (hasCustomCSS) {
				dispatch(fetchCustomCSS())
				.then((customCSS: string) => {
					// Check for HTML response with doctype. Potential improvement: use css validator
					if (customCSS.indexOf('<!doctype html>') === -1) {
						dispatch(setCustomCSS(customCSS));
					}
				});
			}
		});
};

const loadPreviousStates = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void | {}> => {
	return Promise.resolve(dispatch(closeDownloadAllPopupIfNeeded()))
		.then(() => dispatch(loadOfflineManager()))
		.then(() => dispatch(loadPendingTasksManager()))
		.then(() => dispatch(loadPreviousDownloads()))
		.then(() => dispatch(loadPreviousSelection()))
		.then(() => dispatch(loadReadStatusStore()))
		.then(() => dispatch(loadNewItemsStore()));
};

const loadPreviousHomeLogo = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const previousOfflineManagerState = await IndexedDBManager.getStoreOfflineManager();

	if (previousOfflineManagerState) {
		if (previousOfflineManagerState.homeThumb) {
			dispatch({
				type: OfflineManagerActionKeys.INIT_HOME_THUMB,
				homeThumb: previousOfflineManagerState.homeThumb,
			});
		}
	}
};

const fetchTemplateOrLoadPreviousOne = () => async (): Promise<void | {}> => {
	if (UserAgentUtils.isMobileApp && !UserAgentUtils.isOnline()) {
		const cachedTemplate = await getCachedTemplate();

		if (cachedTemplate) {
			return cachedTemplate;
		}
	}
	return Utils.fetchJson(`./.gallery/config/template.json`, 'GET', undefined, true);
};

const fetchCustomCSS = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void | {}> => {
	return Utils.fetchText(`./.gallery/config/custom.css`, 'GET', undefined, true);
};

export const initStatistics = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void | {}> => {
	if (UserAgentUtils.isOnline()) {
		return fetchStatistics()
			.catch(response => {
				return Promise.resolve(undefined);
			});
	}

	fetchStatisticsWhenOnline();
	return Promise.resolve(undefined);
};

const fetchStatisticsWhenOnline = (): void => {
	UserAgentUtils.subscribeToConnectionIsUp(
		() => {
			fetchStatistics();
		},
		true
	);
};

const fetchStatistics = (): Promise<void> => {
	return ApiUtils.getStatistics()
		.then((statisticsConfig: string) => {
			StatsUtils.updateConfig(statisticsConfig);
		});
};

export const fetchRemoteTemplate = () => async (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void | {}> => {
	const { galleryInfos, authentication, multiAppAuthenticationInfos } = getState();
	const { mode } = authentication.userInfos;
	const { userLoggedOut } = multiAppAuthenticationInfos;
	const prefix = IndexedDBManager.getGalleryPrefix();

	if (UserAgentUtils.isMobileApp) {
		if (!UserAgentUtils.isOnline()) {
			const cachedTemplate = await getCachedTemplate();
			const cachedCustomCSS = await getCachedCustomCSS();

			if (cachedTemplate) {
				if (AuthUtils.isSaml(mode) && UserAgentUtils.isIOSApp()) {
					dispatch(updateCredentials({
						mode: { ...(mode as AuthenticationMode), onCache: true }
					}));
				}

				dispatch(setCustomCSS(cachedCustomCSS));
				return dispatch(receiveRemoteTemplate(cachedTemplate, userLoggedOut));
			}

			console.error('NO CACHED TEMPLATE FOUND! Prefix:', prefix, 'Config applicationGallery:', galleryInfos.config && galleryInfos.config.applicationGallery);
			dispatch(createNotification('info.offline_no_config', { id: galleryInfos.config.applicationGallery }, -1));
			dispatch(sendInitializationError());

			throw undefined;
		} else {
			const { multiAppAuthenticationInfos, appInfos } = getState();
			const isMultiApp = Utils.isMultiApp(appInfos);
			const server = isMultiApp ? multiAppAuthenticationInfos.server : undefined;

			await dispatch(areCredentialsNeeded());

			try {
				const template: Api.Template = await ApiUtils.getTemplate(server);

				if (AuthUtils.isSaml(mode) && (AuthUtils.isNotConnected(mode) || AuthUtils.isConnecting(mode))) {
					dispatch(updateCredentials({
						mode: { ...(mode as AuthenticationMode), status: ConnectionStatus.CONNECTED }
					}));
				}

				if (!template.menu) {
					template.menu = isMultiApp ? defaultAppMenu : defaultGalleryMenu;
				}

				dispatch(receiveRemoteTemplate(template, userLoggedOut));
				await dispatch(downloadBrandThumbIfNeeded(BrandThumbConfigLocation.REMOTE_CONFIG, template, true));
			} catch (err) {
				throw err;
			}

			const { hasCustomCSS } = getState().galleryInfos.template;

			if (hasCustomCSS) {
				ApiUtils.getCustomCSS(server)
					.then((customCSS: string) => {
						dispatch(setCustomCSS(customCSS));
					});
			}

			return;
		}
	}

	return;
};

const getCachedTemplate = async (): Promise<Api.Template | null> => {
	const cachedGalleryInfosState = await IndexedDBManager.getGalleryInfos();

	return cachedGalleryInfosState ? cachedGalleryInfosState.template : null;
};

const getCachedCustomCSS = async (): Promise<string> => {
	const cachedGalleryInfosState = await IndexedDBManager.getGalleryInfos();

	return cachedGalleryInfosState ? cachedGalleryInfosState.customCSS : '';
};

const RemoveForceSplashScreenAfterDelay = () => (dispatch: Dispatch<StoreState>, getState: () => StoreState): Promise<void> => {
	const { mode } = getState().authentication.userInfos;

	if (!AuthUtils.needReconnecting(mode) || !UserAgentUtils.isOnline()) { // SAML automated reconnection on IOS is done behind the splashscreen
		setTimeout(
			() => {
				dispatch(setForceSplashscreen(false));
			},
			1500
		);
	}
	return Promise.resolve();
};

export const setForceSplashscreen = (isShown: boolean): SetForceSplashScreen => ({
	type: GalleryInfosActionKeys.SET_FORCE_SPLASH_SCREEN,
	isShown
});

export const requestConfig = (): RequestConfigAction => ({ type: GalleryInfosActionKeys.REQUEST_CONFIG });

export const receiveConfig = (data: Api.Config): ReceiveConfigAction => ({
	type: GalleryInfosActionKeys.RECEIVE_CONFIG,
	data
});

const receiveLocalTemplate = (data: Api.Template): ReceiveLocalTemplateAction => ({
	type: GalleryInfosActionKeys.RECEIVE_LOCAL_TEMPLATE,
	data
});

const receiveRemoteTemplate = (data: Api.Template, isAfterLogout: boolean = false): ReceiveRemoteTemplateAction => ({
	type: GalleryInfosActionKeys.RECEIVE_REMOTE_TEMPLATE,
	data,
	isAfterLogout
});

export const setCustomCSS = (data: string): SetCustomCSSAction => ({
	type: GalleryInfosActionKeys.SET_CUSTOM_CSS,
	data
});



// WEBPACK FOOTER //
// ./node_modules/tslint-loader!./src/containers/Gallery/actions.ts